import { observer } from "mobx-react";
import React, { FC, useEffect, useRef, useState } from "react";
import { Alert, Box, IconButton, Stack, Typography, useTheme } from "@mui/material";

import { CopyIcon } from "../../../icons";
import { CheckIcon } from "../../../icons/CheckIcon";
import { useAppStore } from "../../../../hooks/useAppStore";

const COPIED_RESET_MS = 2000;

const RotatedSecretBox: FC = observer(() => {
  const theme = useTheme();
  const appStore = useAppStore();

  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  if (!appStore.rotatedApiKeySecret) return null;

  const handleCopySecret = async () => {
    if (!appStore.rotatedApiKeySecret) return;

    try {
      await navigator.clipboard.writeText(appStore.rotatedApiKeySecret);
      setCopied(true);

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, COPIED_RESET_MS);
    } catch {
      /** Clipboard write failed; nothing to recover from here */
    }
  };

  return (
    <Stack spacing={1.5}>
      <Alert severity="warning" sx={{ width: "100%", maxWidth: "none" }}>
        This is the only time the full key will be shown — copy it now. The
        previous key has been deactivated and will stop working immediately.
      </Alert>

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "monospace",
            fontSize: "0.875rem",
            wordBreak: "break-all",
          }}
        >
          {appStore.rotatedApiKeySecret}
        </Typography>
        <IconButton
          onClick={handleCopySecret}
          size="small"
          title={copied ? "Copied!" : "Copy to clipboard"}
        >
          {copied ? (
            <CheckIcon size={18} className="text-green-600" />
          ) : (
            <CopyIcon size={18} />
          )}
        </IconButton>
      </Box>
    </Stack>
  );
});

export default RotatedSecretBox;
